import React, { useState, useEffect, useRef } from "react";
import {
    User,
    Camera,
    PenTool,
    Save,
    Percent,
    DollarSign,
    Settings,
    Check,
    Loader2,
    ShieldCheck,
    ArrowLeft,
    AlertCircle
} from "lucide-react";
import "./Profile.css";
import { getGroupDetails, updateGroup, updateGroupAssets } from "../../services/groupService";
import { useNavigate } from "react-router-dom";
import { IMAGE_BASE_URL } from "../../api/apiClient";

const Profile = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        GroupName: "MNC acadamy",
        GroupCode: "MNCA",
        taxPercent: 18.00,
        lateFeeType: "FLAT",
        lateFeePercent: 10.00,
        isCompound: false,
        isTaxApplicable: true // UI only state
    });

    const [assets, setAssets] = useState({
        logo: null,
        signature: null,
        logoPreview: null,
        signaturePreview: null
    });

    const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "success" });

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async (isSilent = false) => {
        try {
            if (!isSilent) setIsLoading(true);
            const res = await getGroupDetails();
            // Handle both array and single object responses
            let data = null;
            if (res.success) {
                if (Array.isArray(res.data) && res.data.length > 0) {
                    data = res.data[0];
                } else if (res.data && !Array.isArray(res.data)) {
                    data = res.data;
                }
            }

            if (data) {
                setFormData({
                    GroupName: data.GroupName || "",
                    GroupCode: data.GroupCode || "",
                    taxPercent: data.TaxPercent || 0,
                    lateFeeType: data.LateFeeType || "FLAT",
                    lateFeePercent: data.LateFeePercent || 0,
                    isCompound: data.IsCompound || false,
                    isTaxApplicable: (data.TaxPercent > 0)
                });

                const logo = data.logoUrl || data.Logo || data.logo || data.LogoUrl || data.logo_url || data.groupLogo || data.GroupLogo;
                const signature = data.Signature || data.signature || data.signatureUrl || data.SignatureUrl || data.signature_url || data.SignaturePath;

                const getFullUrl = (path) => {
                    if (!path) return null;
                    if (path.startsWith('data:') || path.startsWith('http')) return path;
                    // Ensure there's exactly one slash between base and path
                    const cleanBase = IMAGE_BASE_URL.replace(/\/$/, '');
                    const cleanPath = path.startsWith('/') ? path : `/${path}`;
                    return `${cleanBase}${cleanPath}`;
                };

                setAssets(prev => ({
                    logo: null,
                    signature: null,
                    logoPreview: getFullUrl(logo) || prev.logoPreview,
                    signaturePreview: getFullUrl(signature) || prev.signaturePreview
                }));
            }
        } catch (error) {
            console.error("Failed to fetch profile", error);
        } finally {
            setIsLoading(false);
        }
    };

    const logoInputRef = useRef(null);
    const signatureInputRef = useRef(null);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setAssets(prev => ({
                    ...prev,
                    [type]: file,
                    [`${type}Preview`]: reader.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);

            // 1. Update Group Details
            const groupPayload = {
                GroupName: formData.GroupName,
                GroupCode: formData.GroupCode,
                taxPercent: formData.isTaxApplicable ? parseFloat(formData.taxPercent) : 0,
                lateFeeType: formData.lateFeeType,
                lateFeePercent: parseFloat(formData.lateFeePercent),
                isCompound: formData.isCompound
            };

            const detailsRes = await updateGroup(groupPayload);
            if (!detailsRes.success) throw new Error(detailsRes.message || "Failed to update academy details");

            // 2. Update Assets if selected (send data URLs as base64 strings)
            if (assets.logo instanceof File || assets.signature instanceof File) {
                const assetPayload = {};

                if (assets.logo instanceof File && assets.logoPreview) {
                    // Send only the raw base64 part, stripping the data:image/... prefix
                    assetPayload.logoBase64 = assets.logoPreview.split(',')[1] || assets.logoPreview;
                }

                if (assets.signature instanceof File && assets.signaturePreview) {
                    // Send only the raw base64 part, stripping the data:image/... prefix
                    assetPayload.signatureBase64 = assets.signaturePreview.split(',')[1] || assets.signaturePreview;
                }

                if (Object.keys(assetPayload).length > 0) {
                    const assetRes = await updateGroupAssets(assetPayload);
                    if (!assetRes.success) throw new Error(assetRes.message || "Failed to upload logo/signature");
                }
            }

            setPopup({
                show: true,
                title: "Success",
                message: "Profile configuration updated successfully!",
                type: "success"
            });

            // Re-fetch with a slight delay to allow backend to finalize file processing
            setTimeout(() => {
                fetchProfile(true);
            }, 1000);
        } catch (error) {
            console.error("Save failed", error);
            setPopup({
                show: true,
                title: "Update Failed",
                message: typeof error === 'string' && error ? error : (error.message || "An unexpected error occurred"),
                type: "error"
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="profile-loader-container">
                <Loader2 className="animate-spin" size={48} color="#16a34a" />
                <p>Loading your profile...</p>
            </div>
        );
    }

    return (
        <div className="profile-container">
            {/* MOBILE HEADER BAR */}
            <div className="mobile-header-bar">
                <button className="mobile-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={20} />
                </button>
                <div className="mobile-title">Academy Profile</div>
            </div>


            <div className="profile-grid">
                {/* Left: Assets Section */}
                <aside className="assets-card">
                    <div className="asset-upload-group">
                        <span className="asset-label">Academy Logo</span>
                        <div
                            className="image-preview-container"
                            onClick={() => logoInputRef.current?.click()}
                        >
                            {assets.logoPreview ? (
                                <img src={assets.logoPreview} alt="Logo Preview" className="preview-img" />
                            ) : (
                                <div className="upload-placeholder">
                                    <div className="icon-wrap">
                                        <Camera size={28} />
                                    </div>
                                    <span>Upload Academy Logo</span>
                                    <p>PNG, JPG up to 5MB</p>
                                </div>
                            )}
                            <input
                                type="file"
                                ref={logoInputRef}
                                hidden
                                accept="image/*"
                                onChange={(e) => handleFileChange(e, 'logo')}
                            />
                        </div>
                    </div>

                    <div className="asset-upload-group">
                        <span className="asset-label">Authorized Signature</span>
                        <div
                            className="image-preview-container signature-preview"
                            onClick={() => signatureInputRef.current?.click()}
                        >
                            {assets.signaturePreview ? (
                                <img src={assets.signaturePreview} alt="Signature Preview" className="preview-img" />
                            ) : (
                                <div className="upload-placeholder">
                                    <div className="icon-wrap">
                                        <PenTool size={28} />
                                    </div>
                                    <span>Upload Authorized Signature</span>
                                    <p>Clear background recommended</p>
                                </div>
                            )}
                            <input
                                type="file"
                                ref={signatureInputRef}
                                hidden
                                accept="image/*"
                                onChange={(e) => handleFileChange(e, 'signature')}
                            />
                        </div>
                    </div>
                </aside>

                {/* Right: Details Section */}
                <main className="details-card">
                    <h2><Settings size={22} color="#16a34a" /> General Configuration</h2>

                    <div className="form-layout">
                        <div className="form-row">
                            <div className="input-group">
                                <label>Academy Name</label>
                                <input
                                    type="text"
                                    name="GroupName"
                                    value={formData.GroupName}
                                    onChange={handleInputChange}
                                    className="premium-input"
                                    placeholder="Enter academy name"
                                />
                            </div>
                            <div className="input-group">
                                <label>Short Code</label>
                                <input
                                    type="text"
                                    name="GroupCode"
                                    value={formData.GroupCode}
                                    onChange={handleInputChange}
                                    className="premium-input"
                                    placeholder="e.g. MNCA"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="input-group">
                                <label>Tax Configuration</label>
                                <div
                                    className={`checkbox-group ${formData.isTaxApplicable ? 'active' : ''}`}
                                    onClick={() => setFormData(p => ({ ...p, isTaxApplicable: !p.isTaxApplicable }))}
                                >
                                    <div className="checkbox-custom">
                                        {formData.isTaxApplicable && <Check size={14} color="white" />}
                                    </div>
                                    <span className="checkbox-text">GST Applicable</span>
                                </div>
                            </div>
                            {formData.isTaxApplicable && (
                                <div className="input-group">
                                    <label>Tax Percentage (%)</label>
                                    <div className="premium-input-wrap">
                                        <input
                                            type="number"
                                            name="taxPercent"
                                            value={formData.taxPercent}
                                            onChange={handleInputChange}
                                            className="premium-input"
                                            placeholder="18.00"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="form-row">
                            <div className="input-group">
                                <label>Late Fee Structure</label>
                                <div className="radio-row">
                                    <div
                                        className={`radio-option ${formData.lateFeeType === 'FLAT' ? 'active' : ''}`}
                                        onClick={() => setFormData(p => ({ ...p, lateFeeType: 'FLAT' }))}
                                    >
                                        <DollarSign size={16} /> Flat Fee
                                    </div>
                                    <div
                                        className={`radio-option ${formData.lateFeeType === 'PERCENTAGE' ? 'active' : ''}`}
                                        onClick={() => setFormData(p => ({ ...p, lateFeeType: 'PERCENTAGE' }))}
                                    >
                                        <Percent size={16} /> Percentage
                                    </div>
                                </div>
                            </div>
                            <div className="input-group">
                                <label>
                                    {formData.lateFeeType === 'FLAT' ? 'Late Fee Amount' : 'Late Fee Percentage'}
                                </label>
                                <input
                                    type="number"
                                    name="lateFeePercent"
                                    value={formData.lateFeePercent}
                                    onChange={handleInputChange}
                                    className="premium-input"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        <div className="input-group">
                            <div
                                className={`checkbox-group ${formData.isCompound ? 'active' : ''}`}
                                onClick={() => setFormData(p => ({ ...p, isCompound: !p.isCompound }))}
                            >
                                <div className="checkbox-custom">
                                    {formData.isCompound && <Check size={14} color="white" />}
                                </div>
                                <div className="checkbox-text-wrap">
                                    <span className="checkbox-text">Compound Late Fee</span>
                                    <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                                        Check this to apply late fees on top of previous outstanding balances.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="save-actions">
                        <button
                            className="btn-save-premium"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <><Loader2 className="animate-spin" size={20} /> Saving Changes...</>
                            ) : (
                                <><ShieldCheck size={20} /> Save Configuration</>
                            )}
                        </button>
                    </div>
                </main>
            </div>

            {/* MODERN POPUP */}
            {popup.show && (
                <div className="modal-overlay" style={{ zIndex: 10000 }}>
                    <div className="status-popup-card">
                        <div className={`status-icon ${popup.type}`}>
                            {popup.type === 'success' ? <Check size={32} /> : <AlertCircle size={32} />}
                        </div>
                        <h3>{popup.title}</h3>
                        <p>{popup.message}</p>
                        <button
                            className="btn-popup-close"
                            onClick={() => setPopup({ ...popup, show: false })}
                        >
                            Continue
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
